import React from 'react'
import CustomSelect from '@/components/UI/Select'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  Tooltip,
} from 'chart.js'
import { Chart } from 'react-chartjs-2'
import ExportReportShared from '../../../../../components/UI/ExportReportShared/ExportReportShared'
import { useTranslation } from 'react-i18next'
import ChartLegend from '../../Index/components/ChartLegend'
import { Stack, Typography } from '@mui/material'
import CustomCard from '@/components/UI/CustomCard'
import dayjs from 'dayjs'

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  BarController,
  PointElement,
  LineElement,
  LineController,
  Tooltip,
)

interface UsageTrendsChartViewProps {
  loading: boolean
  sessionsData: any[]
  filtersData: {
    filter_from_date: any
    filter_to_date: any
  }
  frequency: {
    value: string
    label: string
  }
  maxValue: number
  isOrganisation?: boolean
  onFrequencyChange: (option: any) => void
}

const UsageTrendsChartView: React.FC<UsageTrendsChartViewProps> = ({
  loading,
  sessionsData,
  filtersData,
  frequency,
  maxValue,
  isOrganisation = false,
  onFrequencyChange,
}) => {
  const { t } = useTranslation('translation')

  // Chart.js configuration - using 'any' to support mixed chart types
  const chartOptions: any = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'index' as const,
      intersect: false,
    },
    plugins: {
      legend: {
        display: false, // We'll use custom legend
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        titleColor: '#fff',
        bodyColor: '#fff',
        borderColor: '#ddd',
        borderWidth: 1,
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Date',
          color: '#666',
        },
        ticks: {
          color: '#666',
          maxRotation: 45,
          maxTicksLimit: 8, // Limit number of ticks to avoid crowding
        },
        grid: {
          color: '#EAF0F4',
        },
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'No of sessions',
          color: '#666',
        },
        ticks: {
          color: '#666',
          padding: 5,
          stepSize: 1,
          callback: function (value: any) {
            if (Number.isInteger(value)) {
              return value
            }
            return null
          },
        },
        grid: {
          color: '#EAF0F4',
        },
        beginAtZero: true,
        max: maxValue ? Math.ceil(maxValue * 1.2) : undefined, // Add 20% padding above highest value
      },
      y1: {
        type: 'linear' as const,
        display: false, // Hidden axis for line chart
        position: 'right' as const,
        ticks: {
          stepSize: 1,
          callback: function (value: any) {
            if (Number.isInteger(value)) {
              return value
            }
            return null
          },
        },
        beginAtZero: true,
        max: maxValue ? Math.ceil(maxValue * 1.2) : undefined, // Add 20% padding above highest value
      },
    },
  }

  const getChartData = () => {
    const labels = sessionsData.map((item: any, index: number) => {
      if (frequency.value === 'weekly') {
        // For weekly data, show date range
        const weekDate = item.week || item.date
        const weekStart = dayjs(weekDate)
        const weekEnd = dayjs(weekStart).add(6, 'days')
        return `${weekStart.format('MMM DD')} - ${weekEnd.format('MMM DD')}`
      } else {
        // For daily data, show individual date
        return dayjs(item.date).format('MMM DD')
      }
    })

    return {
      labels,
      datasets: [
        {
          type: 'bar' as const,
          label: t('Sessions'),
          data: sessionsData.map((item: any) => item.session_count || 0),
          backgroundColor: 'rgba(0, 172, 255, 0.8)',
          borderColor: 'rgba(0, 172, 255, 1)',
          borderWidth: 1,
          borderRadius: 4,
          yAxisID: 'y',
        },
        {
          type: 'line' as const,
          label: t('Active Users'),
          data: sessionsData.map((item: any) => item.user_count || 0),
          borderColor: '#5D09E6',
          backgroundColor: 'rgba(93, 9, 230, 0.1)',
          borderWidth: 2,
          pointBackgroundColor: '#fff',
          pointBorderColor: '#5D09E6',
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          tension: 0.2,
          fill: false,
          yAxisID: 'y1',
        },
      ],
    }
  }

  return (
    <CustomCard className="tw-p-8" id="usage_trends">
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        useFlexGap
        mb={2}
      >
        <Typography variant="subtitle2" component="h2">
          {t('Usage Trends')}
        </Typography>

        {!loading && (
          <div className="tw-flex tw-gap-2 tw-items-center tw-w-52">
            <CustomSelect
              value={frequency.value}
              options={[
                { value: 'daily', label: t('Daily') },
                { value: 'weekly', label: t('Weekly') },
              ]}
              onChange={(_value, option) => {
                onFrequencyChange(option)
              }}
            />

            <ExportReportShared
              exportName="usage_trends"
              name={`${isOrganisation ? 'org-' : ''}${
                frequency.value === 'weekly'
                  ? 'weekly-active-users-sessions'
                  : 'active-users-sessions'
              }`}
              filtersData={filtersData}
              removedKeyFromObject="type"
            />
          </div>
        )}
      </Stack>
      {loading && (
        <div className="d-flex justify-content-center">
          <div className="spinner-border spinner-border-lg text-primary"></div>
        </div>
      )}
      {!loading && (
        <div>
          <Stack direction="row" mb={2} pl={2} gap={2}>
            <ChartLegend title={t('Active users')} color="#5D09E6" />
            <ChartLegend title={t('Sessions')} color="rgba(0, 172, 255, 1)" />
          </Stack>
          <div style={{ width: '100%', height: '350px' }}>
            <Chart type="bar" data={getChartData()} options={chartOptions} />
          </div>
        </div>
      )}
    </CustomCard>
  )
}

export default UsageTrendsChartView
